// B2B Price List Management
import { useState, useCallback, useMemo } from "react";
import { Tag, Plus, Eye, Pencil, AlertCircle, Layers, Percent } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  usePriceLists,
  useCreatePriceList,
  usePriceListItems,
} from "@/hooks/admin/useB2B";
import type {
  PriceList,
  PriceListItem,
  CreatePriceListInput,
} from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CUSTOMER_GROUP_OPTIONS = [
  { value: "détail", label: "Détail" },
  { value: "gros", label: "Gros" },
  { value: "super-gros", label: "Super-gros" },
] as const;

const EMPTY_FORM: CreatePriceListInput = {
  name: "",
  description: "",
  customer_group: "",
  discount_percent: 0,
};

// ---------------------------------------------------------------------------
// Price list items modal
// ---------------------------------------------------------------------------

interface ItemsModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly priceList: PriceList | null;
}

function PriceListItemsModal({ open, onClose, priceList }: ItemsModalProps) {
  const { data, isLoading } = usePriceListItems(priceList?.id ?? "");
  const items = data?.items ?? [];

  const columns: readonly ColumnDef<PriceListItem>[] = useMemo(
    () => [
      {
        id: "sku",
        header: "SKU",
        cell: (i) => (
          <span className="font-mono text-xs text-stone-500">{i.sku}</span>
        ),
      },
      {
        id: "product_name",
        header: "Produit",
        cell: (i) => (
          <span className="font-medium text-stone-900">{i.product_name}</span>
        ),
      },
      {
        id: "base_price_fcfa",
        header: "Prix de base",
        cell: (i) => (
          <span className="text-sm text-stone-600">
            {i.base_price_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
        ),
      },
      {
        id: "list_price_fcfa",
        header: "Prix liste",
        cell: (i) => (
          <span className="text-sm font-medium text-stone-900">
            {i.list_price_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
        ),
      },
      {
        id: "discount_percent",
        header: "Remise",
        cell: (i) => {
          const badgeColor =
            i.discount_percent >= 15
              ? "bg-emerald-100 text-emerald-700"
              : i.discount_percent >= 5
                ? "bg-amber-100 text-amber-700"
                : "bg-stone-100 text-stone-600";
          return (
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeColor}`}
            >
              -{i.discount_percent}%
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={`Produits — ${priceList?.name ?? ""}`}
      size="xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      {isLoading ? (
        <div className="py-8 text-center text-sm text-stone-500">
          Chargement...
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-stone-500">
          Aucun produit dans cette liste de prix.
        </div>
      ) : (
        <>
          <div className="mb-3 text-xs text-stone-500">
            {items.length} produit(s) dans cette liste
          </div>
          <AdminDataTable data={items} columns={columns} isLoading={false} />
        </>
      )}
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Create form modal
// ---------------------------------------------------------------------------

interface CreateModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (input: CreatePriceListInput) => Promise<void>;
  readonly isSaving: boolean;
}

function CreatePriceListModal({
  open,
  onClose,
  onSave,
  isSaving,
}: CreateModalProps) {
  const [form, setForm] = useState<CreatePriceListInput>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setError(null);
    onClose();
  }, [onClose]);

  const update = useCallback(
    <K extends keyof CreatePriceListInput>(
      key: K,
      value: CreatePriceListInput[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) {
      setError("Le nom est requis.");
      return;
    }
    if (!form.customer_group.trim()) {
      setError("Le groupe client est requis.");
      return;
    }
    if (form.discount_percent < 0 || form.discount_percent > 100) {
      setError("La remise doit être entre 0 et 100%.");
      return;
    }
    setError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [form, onSave, handleClose]);

  const inputClass =
    "h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100";
  const labelClass = "mb-1.5 block text-sm font-medium text-stone-700";

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvelle liste de prix"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Creer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Nom</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Ex : Tarif Grossiste Premium"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={2}
            placeholder="Description optionnelle"
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Groupe client</label>
            <select
              value={form.customer_group}
              onChange={(e) => update("customer_group", e.target.value)}
              className={inputClass}
            >
              <option value="">Selectionner un groupe</option>
              {CUSTOMER_GROUP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Remise globale (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={form.discount_percent}
              onChange={(e) =>
                update("discount_percent", Number(e.target.value))
              }
              className={inputClass}
            />
          </div>
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function PriceLists() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewingItems, setViewingItems] = useState<PriceList | null>(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string | null>>({
    status: null,
  });

  const params = useMemo(
    () => ({
      search: search || undefined,
    }),
    [search],
  );

  const { data, isLoading, isError, refetch } = usePriceLists(params);
  const createPriceList = useCreatePriceList();

  const priceLists = data?.price_lists ?? [];

  // Client-side filter for status since API may not support it
  const filteredLists = useMemo(() => {
    if (!filters.status) return priceLists;
    const isActive = filters.status === "active";
    return priceLists.filter((pl) => pl.is_active === isActive);
  }, [priceLists, filters.status]);

  const kpiData = useMemo(() => {
    const total = priceLists.length;
    const active = priceLists.filter((pl) => pl.is_active).length;
    const inactive = total - active;
    const avgDiscount =
      total > 0
        ? priceLists.reduce((s, pl) => s + pl.discount_percent, 0) / total
        : 0;
    const totalProducts = priceLists.reduce((s, pl) => s + pl.product_count, 0);
    return { total, active, inactive, avgDiscount, totalProducts };
  }, [priceLists]);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
  }, []);

  const handleSave = useCallback(
    async (input: CreatePriceListInput) => {
      await createPriceList.mutateAsync(input);
      setIsCreateOpen(false);
    },
    [createPriceList],
  );

  const filterConfigs = useMemo(
    () => [
      {
        key: "status",
        label: "Statut",
        type: "select" as const,
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
    ],
    [],
  );

  const columns: readonly ColumnDef<PriceList>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Nom",
        cell: (pl) => (
          <div>
            <p className="font-medium text-stone-900">{pl.name}</p>
            {pl.description && (
              <p className="mt-0.5 text-xs text-stone-500 line-clamp-1">
                {pl.description}
              </p>
            )}
          </div>
        ),
      },
      {
        id: "customer_group",
        header: "Groupe client",
        cell: (pl) => {
          const groupColors: Record<string, string> = {
            "détail": "bg-blue-100 text-blue-700",
            "gros": "bg-amber-100 text-amber-700",
            "super-gros": "bg-emerald-100 text-emerald-700",
          };
          const color = groupColors[pl.customer_group.toLowerCase()] ?? "bg-stone-100 text-stone-700";
          return (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>
              {pl.customer_group}
            </span>
          );
        },
      },
      {
        id: "discount_percent",
        header: "Remise globale",
        cell: (pl) => (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 rounded-full bg-stone-100">
              <div
                className="h-1.5 rounded-full bg-amber-500"
                style={{ width: `${Math.min(pl.discount_percent, 100)}%` }}
              />
            </div>
            <span className="text-sm font-medium text-amber-700">
              -{pl.discount_percent}%
            </span>
          </div>
        ),
      },
      {
        id: "product_count",
        header: "Produits",
        cell: (pl) => (
          <span className="text-sm text-stone-600">{pl.product_count}</span>
        ),
      },
      {
        id: "created_at",
        header: "Creee le",
        cell: (pl) => (
          <span className="text-sm text-stone-500">
            {new Date(pl.created_at).toLocaleDateString("fr-SN")}
          </span>
        ),
      },
      {
        id: "is_active",
        header: "Statut",
        cell: (pl) => (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              pl.is_active
                ? "bg-emerald-100 text-emerald-700"
                : "bg-stone-100 text-stone-500"
            }`}
          >
            {pl.is_active ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (pl) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setViewingItems(pl)}
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Voir les produits"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Modifier"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Tag className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Listes de prix
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement..."
                  : `${priceLists.length} liste(s) de prix`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouvelle liste de prix
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total listes"
            value={kpiData.total}
            format="number"
            icon={<Tag className="h-4 w-4" />}
          />
          <KPICard
            label="Actives"
            value={kpiData.active}
            format="number"
            icon={<Layers className="h-4 w-4" />}
          />
          <KPICard
            label="Remise moyenne"
            value={kpiData.avgDiscount}
            format="percent"
            icon={<Percent className="h-4 w-4" />}
          />
          <KPICard
            label="Produits couverts"
            value={kpiData.totalProducts}
            format="number"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par nom de liste..."
          />
          <FilterBar
            filters={filterConfigs}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les listes de prix.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Reessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && filteredLists.length === 0 ? (
              <EmptyState
                icon={<Tag className="h-6 w-6" />}
                title="Aucune liste de prix"
                description={
                  search || filters.status
                    ? "Aucune liste ne correspond aux filtres."
                    : "Creez votre premiere liste de prix B2B."
                }
                action={
                  !search && !filters.status
                    ? {
                        label: "Nouvelle liste de prix",
                        onClick: () => setIsCreateOpen(true),
                      }
                    : undefined
                }
              />
            ) : (
              <AdminDataTable
                data={filteredLists}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      <CreatePriceListModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleSave}
        isSaving={createPriceList.isPending}
      />

      <PriceListItemsModal
        open={viewingItems !== null}
        onClose={() => setViewingItems(null)}
        priceList={viewingItems}
      />
    </AdminLayout>
  );
}
